<template>
	<div class="layers" :class="{ compact }">
		<template v-if="isHuntingModeActive && !compact">
			<ProjectPicker class="project-switcher-slot" />
			<div class="project-switcher-separator"></div>
		</template>

		<div v-if="panelMode !== 'saved'" class="header">
			<div class="title">{{ t("title-layers") }}</div>
			<div class="subtitle">{{ t("subtitle-layers") }}</div>
			<button class="close-button" @click="compact ? emit('close') : uiStore.toggleDrawer()">
				<AppIcon :name="compact ? IconName.Close : IconName.PanelLeftCollapse" :size="18" :stroke-width="1.2" />
			</button>
		</div>

		<SavedSearchesPanel
			v-if="panelMode === 'saved'"
			class="content"
			@close="panelMode = 'filters'"
			@select="applySavedSearch"
		/>
		<div v-else class="content" :class="{ 'peek-locked': compact && !expanded }">
			<AccordionRoot
				ref="accordionRootRef"
				:multiple="true"
				:default-value="['opportunites']"
				persistence-key="map-layers-accordion"
			>
				<div class="filterable-layers" data-tour="filterable-layers">
					<AccordionItem
						value="opportunites"
						:title="t('layers.sections.opportunities')"
						class="layer-selectors"
						:active-count="activeOpportunitiesCount"
					>
						<template #header-actions>
							<button
								type="button"
								class="filter-button"
								data-tour-lock="layers"
								:class="{
									'has-active-filters': opportunitiesFiltersCount > 0,
									active: activeFloatingFilter === FloatingFilterKey.Opportunities,
								}"
								:aria-label="t('filters.open-filters')"
								:aria-pressed="activeFloatingFilter === FloatingFilterKey.Opportunities"
								:title="t('filters.open-filters')"
								@click.stop="toggleFloatingFilter(FloatingFilterKey.Opportunities)"
							>
								<AppIcon :name="IconName.Filter" />
							</button>
						</template>
						<template v-if="isProjectScopeDetermined">
							<div v-for="item in opportunityLayerItems" :key="item.id" class="layer-selector">
								<span class="layer-selector-label">
									<img v-if="layerIconDataUri(item.id)" :src="layerIconDataUri(item.id) ?? undefined" alt="" />
									{{ item.label }}
								</span>
								<ArkBaseSwitch
									:checked="isOpportunityLayerVisible(item.id)"
									@change="val => toggleOpportunityLayerItem(item.id, val)"
								/>
							</div>
							<div class="opportunity-legend" :class="{ collapsed: !isLegendExpanded }">
								<button
									type="button"
									class="legend-title"
									:aria-expanded="isLegendExpanded"
									@click="isLegendExpanded = !isLegendExpanded"
								>
									<AppIcon :name="IconName.ChevronDownSmall" :size="8" class="legend-chevron" />
									{{ t("filters.legend-status") }}
								</button>
								<template v-if="isLegendExpanded">
									<div class="legend-row">
										<img :src="badgeLegendUris.new" alt="" />
										<span>{{ t("filters.legend-new") }}</span>
									</div>
									<div class="legend-row">
										<img :src="badgeLegendUris.offer" alt="" />
										<span>{{ t("filters.legend-offer") }}</span>
									</div>
									<template v-if="isInProjectScope">
										<div class="legend-row">
											<img :src="badgeLegendUris.liked" alt="" />
											<span>{{ t("filters.legend-liked") }}</span>
										</div>
										<div class="legend-row">
											<img :src="badgeLegendUris.discarded" alt="" />
											<span>{{ t("filters.legend-discarded") }}</span>
										</div>
									</template>
								</template>
							</div>
						</template>
					</AccordionItem>

					<AccordionItem
						value="cadastre"
						:title="t('layers.sections.cadastre')"
						class="layer-selectors"
						:expandable="false"
					>
						<template #header-actions>
							<ArkBaseSwitch
								:checked="_visibleLayers.includes('cadastre')"
								@change="val => updateSelection('cadastre', val)"
							/>
							<button
								type="button"
								class="filter-button"
								data-tour-lock="layers"
								:class="{
									'has-active-filters': cadastreFiltersCount > 0,
									active: activeFloatingFilter === FloatingFilterKey.Cadastre,
								}"
								:aria-label="t('filters.open-filters')"
								:aria-pressed="activeFloatingFilter === FloatingFilterKey.Cadastre"
								:title="t('filters.open-filters')"
								@click.stop="toggleFloatingFilter(FloatingFilterKey.Cadastre)"
							>
								<AppIcon :name="IconName.Filter" />
							</button>
						</template>
					</AccordionItem>

					<AccordionItem
						v-if="isUserLoggedIn && currentUser.has(Permission.TransactionRead)"
						value="transaction"
						:title="t('layers.sections.transaction')"
						class="layer-selectors"
						:expandable="false"
					>
						<template #header-actions>
							<ArkBaseSwitch
								:checked="isTransactionLayerVisible()"
								@change="val => toggleLayerItem(LayerType.Transaction, val)"
							/>
							<button
								type="button"
								class="filter-button"
								data-tour-lock="layers"
								:class="{
									'has-active-filters': transactionsFiltersCount > 0,
									active: activeFloatingFilter === FloatingFilterKey.Transactions,
								}"
								:aria-label="t('filters.open-filters')"
								:aria-pressed="activeFloatingFilter === FloatingFilterKey.Transactions"
								:title="t('filters.open-filters')"
								@click.stop="toggleFloatingFilter(FloatingFilterKey.Transactions)"
							>
								<AppIcon :name="IconName.Filter" />
							</button>
						</template>
					</AccordionItem>
				</div>

				<div v-if="compact" ref="peekEndEl" aria-hidden="true" class="peek-end-marker"></div>

				<AccordionItem
					value="limites"
					:title="t('layers.sections.territorial-boundaries')"
					class="layer-selectors"
					:active-count="activeAdministrativeBoundariesCount"
				>
					<div class="layer-selector">
						<span>{{ t("layers.administrative-divisions/postal-codes") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('administrative-divisions/postal-codes')"
							@change="val => updateSelection('administrative-divisions/postal-codes', val)"
						/>
					</div>
					<div class="layer-selector">
						<span>{{ t("layers.administrative-divisions/regions") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('administrative-divisions/regions')"
							@change="val => updateSelection('administrative-divisions/regions', val)"
						/>
					</div>
					<div class="layer-selector">
						<span>{{ t("layers.administrative-divisions/metropolitan-areas") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('administrative-divisions/metropolitan-areas')"
							@change="val => updateSelection('administrative-divisions/metropolitan-areas', val)"
						/>
					</div>
					<div class="layer-selector">
						<span>{{ t("layers.administrative-divisions/rcms") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('administrative-divisions/rcms')"
							@change="val => updateSelection('administrative-divisions/rcms', val)"
						/>
					</div>
					<div class="layer-selector">
						<span>{{ t("layers.administrative-divisions/municipalities") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('administrative-divisions/municipalities')"
							@change="val => updateSelection('administrative-divisions/municipalities', val)"
						/>
					</div>
					<div class="layer-selector">
						<span>{{ t("layers.administrative-divisions/boroughs") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('administrative-divisions/boroughs')"
							@change="val => updateSelection('administrative-divisions/boroughs', val)"
						/>
					</div>
				</AccordionItem>

				<AccordionItem
					v-if="
						store.currentUser?.has(Permission.WetlandsRead) ||
						store.currentUser?.has(Permission.AgriculturalZonesRead) ||
						store.currentUser?.has(Permission.SoilCapabilityRead) ||
						store.currentUser?.has(Permission.FloodableZonesRead) ||
						store.currentUser?.has(Permission.EndangeredSpeciesRead)
					"
					value="contraintes"
					:title="t('layers.sections.natural-anthropogenic-constraints')"
					class="layer-selectors"
					:active-count="activeConstraintsCount"
				>
					<template #header-actions>
						<LayerInfoPopover
							v-if="constraintsInfoDescription"
							:description="constraintsInfoDescription"
							:legend-label="
								store.currentUser?.has(Permission.EndangeredSpeciesRead) ? t('layers.endangered-species') : undefined
							"
							:legend="
								store.currentUser?.has(Permission.EndangeredSpeciesRead)
									? [
											{ label: t('endangered-species.legend.animals'), icon: legendFillUrl },
											{ label: t('endangered-species.legend.plants'), icon: legendHatchUrl },
										]
									: undefined
							"
						/>
					</template>
					<div v-if="store.currentUser?.has(Permission.WetlandsRead)" class="layer-selector">
						<span>{{ t("layers.wetlands") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('wetlands')"
							@change="val => updateSelection('wetlands', val)"
						/>
					</div>
					<div v-if="store.currentUser?.has(Permission.AgriculturalZonesRead)" class="layer-selector">
						<span>{{ t("layers.agricultural-inclusions-exclusions") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('agricultural-inclusions-exclusions')"
							@change="val => updateSelection('agricultural-inclusions-exclusions', val)"
						/>
					</div>
					<div v-if="store.currentUser?.has(Permission.AgriculturalZonesRead)" class="layer-selector">
						<span>{{ t("layers.agricultural-zones") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('agricultural-zones')"
							@change="val => updateSelection('agricultural-zones', val)"
						/>
					</div>
					<div v-if="store.currentUser?.has(Permission.SoilCapabilityRead)" class="layer-selector">
						<span>{{ t("layers.soil-capability") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('soil-capability')"
							@change="val => updateSelection('soil-capability', val)"
						/>
					</div>
					<div v-if="store.currentUser?.has(Permission.FloodableZonesRead)" class="layer-selector">
						<span>{{ t("layers.floodable-zones") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('floodable-zones')"
							@change="val => updateSelection('floodable-zones', val)"
						/>
					</div>
					<div v-if="store.currentUser?.has(Permission.EndangeredSpeciesRead)" class="layer-selector">
						<span>{{ t("layers.endangered-species") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('endangered-species')"
							@change="val => updateSelection('endangered-species', val)"
						/>
					</div>
					<div
						v-if="
							store.currentUser?.has(Permission.EndangeredSpeciesRead) && _visibleLayers.includes('endangered-species')
						"
						class="layer-subselectors endangered-species-subselectors"
					>
						<div class="layer-subselector">
							<span>{{ t("endangered-species.level.threatened-or-vulnerable") }}</span>
							<ArkBaseSwitch
								:checked="filters.endangeredSpecies.threatenedOrVulnerable"
								:disabled="!_visibleLayers.includes('endangered-species')"
								@change="val => updateBooleanFilter('endangeredSpecies', 'threatenedOrVulnerable', val)"
								color="var(--accent-endangered-species-threatened)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("endangered-species.level.susceptible") }}</span>
							<ArkBaseSwitch
								:checked="filters.endangeredSpecies.susceptible"
								:disabled="!_visibleLayers.includes('endangered-species')"
								@change="val => updateBooleanFilter('endangeredSpecies', 'susceptible', val)"
								color="var(--accent-endangered-species-susceptible)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("endangered-species.level.candidate") }}</span>
							<ArkBaseSwitch
								:checked="filters.endangeredSpecies.candidate"
								:disabled="!_visibleLayers.includes('endangered-species')"
								@change="val => updateBooleanFilter('endangeredSpecies', 'candidate', val)"
								color="var(--accent-endangered-species-candidate)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("endangered-species.level.masked") }}</span>
							<ArkBaseSwitch
								:checked="filters.endangeredSpecies.masked"
								:disabled="!_visibleLayers.includes('endangered-species')"
								@change="val => updateBooleanFilter('endangeredSpecies', 'masked', val)"
								color="var(--accent-endangered-species-masked)"
							/>
						</div>
					</div>
				</AccordionItem>

				<AccordionItem
					v-if="store.currentUser?.has(Permission.LandUseRead)"
					value="affectations"
					:title="t('layers.sections.major-land-uses')"
					class="layer-selectors"
					:active-count="activeLandUsesCount"
				>
					<template #header-actions>
						<LayerInfoPopover
							:description="t('layers.info-popover.major-land-uses-description')"
							:legend-label="t('layers.urban-planning')"
							:legend="[
								{ label: t('major-land-uses.residentielle'), color: '#F0C93B' },
								{ label: t('major-land-uses.commerciale'), color: '#E05252' },
								{ label: t('major-land-uses.industrielle'), color: '#8B5FBF' },
								{ label: t('major-land-uses.multifonctionnelle'), color: '#E8935B' },
								{ label: t('major-land-uses.recreative'), color: '#8FD14F' },
								{ label: t('major-land-uses.rurale'), color: '#D19D61' },
								{ label: t('major-land-uses.extraction'), color: '#8C5A3C' },
								{ label: t('major-land-uses.forestiere'), color: '#309F4D' },
								{ label: t('major-land-uses.conservation'), color: '#42C9AC' },
								{ label: t('major-land-uses.agricole'), color: '#A8C93F' },
								{ label: t('major-land-uses.utilite'), color: '#7C93A8' },
								{ label: t('major-land-uses.aeroportuaire'), color: '#6B7A99' },
								{ label: t('major-land-uses.villegiature'), color: '#6EC6E8' },
								{ label: t('major-land-uses.agro-forestiere'), color: '#7A8C4E' },
								{ label: t('major-land-uses.urbaine'), color: '#979DC4' },
							]"
						/>
					</template>
					<div class="layer-selector">
						<span>{{ t("layers.urban-planning") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('urban-planning')"
							@change="val => updateSelection('urban-planning', val)"
						/>
					</div>
					<div v-if="_visibleLayers.includes('urban-planning')" class="layer-subselectors">
						<div class="layer-subselector">
							<span>{{ t("layers.land-use") }}</span>
							<ArkBaseSwitch
								:checked="_visibleLayers.includes('land-use')"
								@change="val => updateSelection('land-use', val)"
							/>
						</div>
					</div>
				</AccordionItem>

				<AccordionItem
					v-if="store.currentUser?.has(Permission.TransportationRead)"
					value="transportation"
					:title="t('layers.sections.transportation')"
					class="layer-selectors"
					:active-count="activeTransportationCount"
				>
					<template #header-actions>
						<LayerInfoPopover
							v-if="store.currentUser?.has(Permission.TransportationRead)"
							:description="t('layers.info-popover.transportation-description')"
							:legend-label="t('layers.transit-lines')"
							:legend="[
								{ label: t('layers.status.in-use'), line: { dashed: false, opacity: 1 } },
								{ label: t('layers.status.in-construction'), line: { dashed: true, opacity: 150 / 255 } },
								{ label: t('layers.status.proposed'), line: { dashed: true, opacity: 100 / 255 } },
							]"
						/>
					</template>
					<div v-if="store.currentUser?.has(Permission.TransportationRead)" class="layer-selector">
						<span>{{ t("layers.transit-lines") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('transit-lines')"
							@change="val => updateSelection('transit-lines', val)"
						/>
					</div>
					<div
						v-if="store.currentUser?.has(Permission.TransportationRead) && _visibleLayers.includes('transit-lines')"
						class="layer-subselectors transportation-subselectors"
					>
						<div class="layer-subselector">
							<span>{{ t("layers.status.in-use") }}</span>
							<ArkBaseSwitch
								:checked="filters.transitLinesStatus.inUse"
								:disabled="!_visibleLayers.includes('transit-lines')"
								@change="val => updateBooleanFilter('transitLinesStatus', 'inUse', val)"
								color="var(--accent-transit-line-green-bg)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("layers.status.in-construction") }}</span>
							<ArkBaseSwitch
								:checked="filters.transitLinesStatus.inConstruction"
								:disabled="!_visibleLayers.includes('transit-lines')"
								@change="val => updateBooleanFilter('transitLinesStatus', 'inConstruction', val)"
								color="var(--accent-transit-line-orange-bg)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("layers.status.proposed") }}</span>
							<ArkBaseSwitch
								:checked="filters.transitLinesStatus.proposed"
								:disabled="!_visibleLayers.includes('transit-lines')"
								@change="val => updateBooleanFilter('transitLinesStatus', 'proposed', val)"
								color="var(--accent-transit-line-blue-bg)"
							/>
						</div>
					</div>
					<div v-if="store.currentUser?.has(Permission.TransportationRead)" class="layer-selector">
						<span>{{ t("layers.transit-stations") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('transit-stations')"
							@change="val => updateSelection('transit-stations', val)"
						/>
					</div>
					<div
						v-if="store.currentUser?.has(Permission.TransportationRead) && _visibleLayers.includes('transit-stations')"
						class="layer-subselectors"
					>
						<div class="layer-subselector">
							<span>{{ t("layers.status.in-use") }}</span>
							<ArkBaseSwitch
								:checked="filters.transitStationsStatus.inUse"
								:disabled="!_visibleLayers.includes('transit-stations')"
								@change="val => updateBooleanFilter('transitStationsStatus', 'inUse', val)"
								color="var(--accent-transit-line-green-bg)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("layers.status.in-construction") }}</span>
							<ArkBaseSwitch
								:checked="filters.transitStationsStatus.inConstruction"
								:disabled="!_visibleLayers.includes('transit-stations')"
								@change="val => updateBooleanFilter('transitStationsStatus', 'inConstruction', val)"
								color="var(--accent-transit-line-orange-bg)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("layers.status.proposed") }}</span>
							<ArkBaseSwitch
								:checked="filters.transitStationsStatus.proposed"
								:disabled="!_visibleLayers.includes('transit-stations')"
								@change="val => updateBooleanFilter('transitStationsStatus', 'proposed', val)"
								color="var(--accent-transit-line-blue-bg)"
							/>
						</div>
					</div>
				</AccordionItem>

				<AccordionItem
					v-if="
						store.currentUser?.has(Permission.PlanningPolicyRead) ||
						store.currentUser?.has(Permission.UrbanPrioritiesRead) ||
						store.currentUser?.has(Permission.UrbanPerimetersRead) ||
						store.currentUser?.has(Permission.ZoningRead) ||
						store.currentUser?.has(Permission.CityPvDocumentsRead) ||
						showExtendedAccessCta
					"
					value="urbanisation"
					:title="t('layers.sections.urbanization')"
					class="layer-selectors"
					:active-count="activeUrbanizationCount"
				>
					<template #header-actions>
						<LayerInfoPopover
							v-if="urbanizationInfoDescription"
							:description="urbanizationInfoDescription"
							:legend-label="
								store.currentUser?.has(Permission.PlanningPolicyRead) ? t('layers.planning-policy') : undefined
							"
							:legend="
								store.currentUser?.has(Permission.PlanningPolicyRead)
									? [
											{ label: 'PMAD', icon: legendFillUrl },
											{ label: 'SAD', icon: legendOutlineUrl },
											{ label: 'PU', icon: legendHatchUrl },
										]
									: undefined
							"
						/>
					</template>
					<div v-if="store.currentUser?.has(Permission.CityPvDocumentsRead)" class="layer-selector">
						<span>{{ t("layers.city-pv-documents") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('city-pv-documents')"
							@change="val => updateSelection('city-pv-documents', val)"
						/>
					</div>
					<div
						v-else-if="showExtendedAccessCta"
						class="layer-selector locked"
						role="button"
						tabindex="0"
						:title="t('extendedAccess.locked')"
						@click="openExtendedAccessModal(CtaSource.LockedLayer)"
						@keydown.enter="openExtendedAccessModal(CtaSource.LockedLayer)"
					>
						<span class="layer-selector-label">
							{{ t("layers.city-pv-documents") }}
							<AppIcon :name="IconName.Lock" class="lock-icon" :size="14" :stroke-width="1.75" />
						</span>
						<ArkBaseSwitch :checked="false" disabled />
					</div>
					<div v-if="store.currentUser?.has(Permission.PlanningPolicyRead)" class="layer-selector">
						<span>{{ t("layers.planning-policy") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('planning-policy')"
							@change="val => updateSelection('planning-policy', val)"
						/>
					</div>
					<div
						v-else-if="showExtendedAccessCta"
						class="layer-selector locked"
						role="button"
						tabindex="0"
						:title="t('extendedAccess.locked')"
						@click="openExtendedAccessModal(CtaSource.LockedLayer)"
						@keydown.enter="openExtendedAccessModal(CtaSource.LockedLayer)"
					>
						<span class="layer-selector-label">
							{{ t("layers.planning-policy") }}
							<AppIcon :name="IconName.Lock" class="lock-icon" :size="14" :stroke-width="1.75" />
						</span>
						<ArkBaseSwitch :checked="false" disabled />
					</div>
					<div
						v-if="store.currentUser?.has(Permission.PlanningPolicyRead) && _visibleLayers.includes('planning-policy')"
						class="layer-subselectors planning-policy-subselectors"
					>
						<div class="layer-subselector">
							<span>{{ t("planning-policy.level.tod") }}</span>
							<ArkBaseSwitch
								:checked="filters.planningPolicy.tod"
								:disabled="!_visibleLayers.includes('planning-policy')"
								@change="val => updateBooleanFilter('planningPolicy', 'tod', val)"
								color="var(--accent-planning-policy-tod)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("planning-policy.level.ppu") }}</span>
							<ArkBaseSwitch
								:checked="filters.planningPolicy.ppu"
								:disabled="!_visibleLayers.includes('planning-policy')"
								@change="val => updateBooleanFilter('planningPolicy', 'ppu', val)"
								color="var(--accent-planning-policy-ppu)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("planning-policy.level.corridor") }}</span>
							<ArkBaseSwitch
								:checked="filters.planningPolicy.corridor"
								:disabled="!_visibleLayers.includes('planning-policy')"
								@change="val => updateBooleanFilter('planningPolicy', 'corridor', val)"
								color="var(--accent-planning-policy-corridor)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("planning-policy.level.strategic-sector") }}</span>
							<ArkBaseSwitch
								:checked="filters.planningPolicy.strategicSector"
								:disabled="!_visibleLayers.includes('planning-policy')"
								@change="val => updateBooleanFilter('planningPolicy', 'strategicSector', val)"
								color="var(--accent-planning-policy-strategic)"
							/>
						</div>
					</div>
					<div v-if="store.currentUser?.has(Permission.UrbanPerimetersRead)" class="layer-selector">
						<span>{{ t("layers.urban-perimeters") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('urban-perimeters')"
							@change="val => updateSelection('urban-perimeters', val)"
						/>
					</div>
					<div v-if="store.currentUser?.has(Permission.ZoningRead)" class="layer-selector">
						<span>{{ t("layers.zoning") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('zoning')"
							@change="val => updateSelection('zoning', val)"
						/>
					</div>
					<div
						v-else-if="showExtendedAccessCta"
						class="layer-selector locked"
						role="button"
						tabindex="0"
						:title="t('extendedAccess.locked')"
						@click="openExtendedAccessModal(CtaSource.LockedLayer)"
						@keydown.enter="openExtendedAccessModal(CtaSource.LockedLayer)"
					>
						<span class="layer-selector-label">
							{{ t("layers.zoning") }}
							<AppIcon :name="IconName.Lock" class="lock-icon" :size="14" :stroke-width="1.75" />
						</span>
						<ArkBaseSwitch :checked="false" disabled />
					</div>
					<div v-if="store.currentUser?.has(Permission.UrbanPrioritiesRead)" class="layer-selector">
						<span>{{ t("layers.urban-priorities") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('urban-priorities')"
							@change="val => updateSelection('urban-priorities', val)"
						/>
					</div>
					<div
						v-if="store.currentUser?.has(Permission.UrbanPrioritiesRead) && _visibleLayers.includes('urban-priorities')"
						class="layer-subselectors"
					>
						<!-- PMAD -->
						<div class="layer-subselector">
							<span>{{ t("urban-priorities.level.pmad") }}</span>
							<ArkBaseSwitch
								:checked="filters.urbanPriorities.pmad.active || filters.urbanPriorities.pmad.draft"
								:disabled="!_visibleLayers.includes('urban-priorities')"
								@change="val => updateUrbanPriority('pmad', val)"
								color="var(--accent-urban-priority-pmad)"
							/>
						</div>
						<div
							v-if="filters.urbanPriorities.pmad.active || filters.urbanPriorities.pmad.draft"
							class="urban-priority-substatus"
						>
							<div class="urban-priority-substatus-item">
								<span>{{ t("urban-priorities.version.active") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.pmad.active"
									@change="val => updateUrbanPriorityStatus('pmad', 'active', val)"
									color="var(--accent-urban-priority-pmad)"
								/>
							</div>
							<div class="urban-priority-substatus-item draft">
								<span>{{ t("urban-priorities.version.draft") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.pmad.draft"
									@change="val => updateUrbanPriorityStatus('pmad', 'draft', val)"
									color="var(--accent-urban-priority-pmad)"
								/>
							</div>
						</div>

						<!-- SAD -->
						<div class="layer-subselector">
							<span>{{ t("urban-priorities.level.sad") }}</span>
							<ArkBaseSwitch
								:checked="filters.urbanPriorities.sad.active || filters.urbanPriorities.sad.draft"
								:disabled="!_visibleLayers.includes('urban-priorities')"
								@change="val => updateUrbanPriority('sad', val)"
								color="var(--accent-urban-priority-sad)"
							/>
						</div>
						<div
							v-if="filters.urbanPriorities.sad.active || filters.urbanPriorities.sad.draft"
							class="urban-priority-substatus"
						>
							<div class="urban-priority-substatus-item">
								<span>{{ t("urban-priorities.version.active") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.sad.active"
									@change="val => updateUrbanPriorityStatus('sad', 'active', val)"
									color="var(--accent-urban-priority-sad)"
								/>
							</div>
							<div class="urban-priority-substatus-item draft">
								<span>{{ t("urban-priorities.version.draft") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.sad.draft"
									@change="val => updateUrbanPriorityStatus('sad', 'draft', val)"
									color="var(--accent-urban-priority-sad)"
								/>
							</div>
						</div>

						<!-- PU -->
						<div class="layer-subselector">
							<span>{{ t("urban-priorities.level.pu") }}</span>
							<ArkBaseSwitch
								:checked="filters.urbanPriorities.pu.active || filters.urbanPriorities.pu.draft"
								:disabled="!_visibleLayers.includes('urban-priorities')"
								@change="val => updateUrbanPriority('pu', val)"
								color="var(--accent-urban-priority-pu)"
							/>
						</div>
						<div
							v-if="filters.urbanPriorities.pu.active || filters.urbanPriorities.pu.draft"
							class="urban-priority-substatus"
						>
							<div class="urban-priority-substatus-item">
								<span>{{ t("urban-priorities.version.active") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.pu.active"
									@change="val => updateUrbanPriorityStatus('pu', 'active', val)"
									color="var(--accent-urban-priority-pu)"
								/>
							</div>
							<div class="urban-priority-substatus-item draft">
								<span>{{ t("urban-priorities.version.draft") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.pu.draft"
									@change="val => updateUrbanPriorityStatus('pu', 'draft', val)"
									color="var(--accent-urban-priority-pu)"
								/>
							</div>
						</div>

						<!-- PPU -->
						<div class="layer-subselector">
							<span>{{ t("urban-priorities.level.ppu") }}</span>
							<ArkBaseSwitch
								:checked="filters.urbanPriorities.ppu.active || filters.urbanPriorities.ppu.draft"
								:disabled="!_visibleLayers.includes('urban-priorities')"
								@change="val => updateUrbanPriority('ppu', val)"
								color="var(--accent-urban-priority-ppu)"
							/>
						</div>
						<div
							v-if="filters.urbanPriorities.ppu.active || filters.urbanPriorities.ppu.draft"
							class="urban-priority-substatus"
						>
							<div class="urban-priority-substatus-item">
								<span>{{ t("urban-priorities.version.active") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.ppu.active"
									@change="val => updateUrbanPriorityStatus('ppu', 'active', val)"
									color="var(--accent-urban-priority-ppu)"
								/>
							</div>
							<div class="urban-priority-substatus-item draft">
								<span>{{ t("urban-priorities.version.draft") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.ppu.draft"
									@change="val => updateUrbanPriorityStatus('ppu', 'draft', val)"
									color="var(--accent-urban-priority-ppu)"
								/>
							</div>
						</div>

						<!-- PU Interview -->
						<div class="layer-subselector">
							<span>{{ t("urban-priorities.level.pu-interview") }}</span>
							<ArkBaseSwitch
								:checked="filters.urbanPriorities.pu_interview.active || filters.urbanPriorities.pu_interview.draft"
								:disabled="!_visibleLayers.includes('urban-priorities')"
								@change="val => updateUrbanPriority('pu_interview', val)"
								color="var(--accent-urban-priority-pu-interview)"
							/>
						</div>
						<div
							v-if="filters.urbanPriorities.pu_interview.active || filters.urbanPriorities.pu_interview.draft"
							class="urban-priority-substatus"
						>
							<div class="urban-priority-substatus-item">
								<span>{{ t("urban-priorities.version.active") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.pu_interview.active"
									@change="val => updateUrbanPriorityStatus('pu_interview', 'active', val)"
									color="var(--accent-urban-priority-pu-interview)"
								/>
							</div>
							<div class="urban-priority-substatus-item draft">
								<span>{{ t("urban-priorities.version.draft") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.pu_interview.draft"
									@change="val => updateUrbanPriorityStatus('pu_interview', 'draft', val)"
									color="var(--accent-urban-priority-pu-interview)"
								/>
							</div>
						</div>

						<!-- Other -->
						<div class="layer-subselector">
							<span>{{ t("urban-priorities.level.other") }}</span>
							<ArkBaseSwitch
								:checked="filters.urbanPriorities.other.active || filters.urbanPriorities.other.draft"
								:disabled="!_visibleLayers.includes('urban-priorities')"
								@change="val => updateUrbanPriority('other', val)"
								color="var(--accent-urban-priority-other)"
							/>
						</div>
						<div
							v-if="filters.urbanPriorities.other.active || filters.urbanPriorities.other.draft"
							class="urban-priority-substatus"
						>
							<div class="urban-priority-substatus-item">
								<span>{{ t("urban-priorities.version.active") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.other.active"
									@change="val => updateUrbanPriorityStatus('other', 'active', val)"
									color="var(--accent-urban-priority-other)"
								/>
							</div>
							<div class="urban-priority-substatus-item draft">
								<span>{{ t("urban-priorities.version.draft") }}</span>
								<ArkBaseSwitch
									:checked="filters.urbanPriorities.other.draft"
									@change="val => updateUrbanPriorityStatus('other', 'draft', val)"
									color="var(--accent-urban-priority-other)"
								/>
							</div>
						</div>
					</div>
				</AccordionItem>

				<AccordionItem
					v-if="store.currentUser?.has(Permission.DevPotentialRead)"
					value="priorités"
					:title="t('layers.sections.development-potential')"
					class="layer-selectors"
					:active-count="activeDevelopmentPotentialCount"
				>
					<template #header-actions>
						<LayerInfoPopover
							v-if="store.currentUser?.has(Permission.DevPotentialRead)"
							:description="t('layers.info-popover.development-potential-description')"
							:legend-label="t('layers.prospects')"
							:legend="[
								{ label: t('development-potential.high'), color: 'var(--accent-dev-potential-high)' },
								{ label: t('development-potential.medium'), color: 'var(--accent-dev-potential-medium)' },
								{ label: t('development-potential.low'), color: 'var(--accent-dev-potential-low)' },
							]"
						/>
					</template>
					<div v-if="store.currentUser?.has(Permission.DevPotentialRead)" class="layer-selector">
						<span>{{ t("layers.prospects") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes('prospects')"
							@change="val => updateSelection('prospects', val)"
						/>
					</div>
					<div
						v-if="store.currentUser?.has(Permission.DevPotentialRead) && _visibleLayers.includes('prospects')"
						class="layer-subselectors"
					>
						<div class="layer-subselector">
							<span>{{ t("development-potential.high") }}</span>
							<ArkBaseSwitch
								:checked="filters.developmentPotential.high"
								:disabled="!_visibleLayers.includes('prospects')"
								@change="val => updateDevPotentials('high', val)"
								color="var(--accent-dev-potential-high)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("development-potential.medium") }}</span>
							<ArkBaseSwitch
								:checked="filters.developmentPotential.medium"
								:disabled="!_visibleLayers.includes('prospects')"
								@change="val => updateDevPotentials('medium', val)"
								color="var(--accent-dev-potential-medium)"
							/>
						</div>
						<div class="layer-subselector">
							<span>{{ t("development-potential.low") }}</span>
							<ArkBaseSwitch
								:checked="filters.developmentPotential.low"
								:disabled="!_visibleLayers.includes('prospects')"
								@change="val => updateDevPotentials('low', val)"
								color="var(--accent-dev-potential-low)"
							/>
						</div>
					</div>
				</AccordionItem>

				<AccordionItem
					v-if="canViewCompletedAssessments"
					:value="LayerType.CompletedAssessments"
					:title="t('layers.sections.analysis')"
					class="layer-selectors"
					:active-count="_visibleLayers.includes(LayerType.CompletedAssessments) ? 1 : 0"
				>
					<div class="layer-selector">
						<span>{{ t("layers.completed-assessments") }}</span>
						<ArkBaseSwitch
							:checked="_visibleLayers.includes(LayerType.CompletedAssessments)"
							@change="val => updateSelection(LayerType.CompletedAssessments, val)"
							color="var(--accent-completed-assessment)"
						/>
					</div>
				</AccordionItem>
			</AccordionRoot>

			<div v-if="showExtendedAccessCta" class="extended-access-cta">
				<Button
					type="button"
					variant="primary"
					size="m"
					class="action"
					@click="openExtendedAccessModal(CtaSource.Layers)"
				>
					<AppIcon :name="IconName.Layers" :size="16" :stroke-width="1.75" />
					{{ t("extendedAccess.layersCta.action") }}
				</Button>
				<p>{{ t("extendedAccess.layersCta.message") }}</p>
			</div>
			<ExtendedAccessModal v-model:open="isExtendedAccessModalOpen" :source="extendedAccessSource" />

			<LoginCta
				v-if="!isUserLoggedIn"
				class="login-cta"
				subtle
				:source="CtaSource.Layers"
				:title="t('layers.loginCta.title')"
				:message="t('layers.loginCta.message')"
			/>
		</div>

		<div v-if="isUserLoggedIn && panelMode === 'filters' && !compact" class="saved-search-footer">
			<div class="saved-search-section">
				<div class="saved-search-toggle">
					<span class="label">
						<AppIcon :name="IconName.Bookmark" class="icon" />
						{{ t("custom-views.title") }}
					</span>
					<ArkBaseSwitch :checked="savedSearchEnabled" @change="onToggleSavedSearch" />
				</div>

				<Transition name="saved-search-swap" mode="out-in">
					<p v-if="savedSearchEnabled && isRestoringActiveSavedSearch" key="loading" class="saved-search-loading">
						{{ t("custom-views.loading") }}
					</p>

					<SavedSearchCard
						v-else-if="savedSearchEnabled"
						key="card"
						class="nested"
						:show-icon="false"
						v-model:new-search-name="newSearchName"
						:title="currentSearchTitle"
						:summary="currentSearchSummary"
						:is-empty="isEmptySearchState"
						:is-unsaved="isUnsavedSearch"
						:is-modified="isActiveSavedSearchModified"
						:has-active-saved-search="Boolean(activeSavedSearch)"
						:is-naming-search="isNamingSearch"
						:is-saving-search="isSavingSearch"
						:is-updating-search="isUpdatingSearch"
						:save-error="saveError"
						:saved-search-count="savedSearches.length"
						@reset="resetCurrentSearch"
						@cancel-changes="cancelSavedSearchChanges"
						@cancel-save="cancelSave"
						@save="saveCurrentSearch"
						@open-saved-searches="openSavedSearches"
						@update-search="updateCurrentSearch"
						@start-save="startSave"
					/>
				</Transition>
			</div>
		</div>

		<div v-if="compact && activeFloatingFilter" class="compact-filter-view">
			<div class="compact-filter-header">
				<button type="button" class="back-button" :aria-label="t('filters.back')" @click="activeFloatingFilter = null">
					<AppIcon :name="IconName.ArrowLeft" :size="16" :stroke-width="1.2" />
				</button>
				<span class="title">{{ floatingFilterTitle }}</span>
				<Button
					v-if="activeFloatingFilterCount > 0"
					variant="ghost"
					size="xs"
					shape="pill"
					class="reset-filters-button"
					@click="resetActiveFloatingFilter"
				>
					{{ t("filters.clear-filters") }}
				</Button>
			</div>
			<div class="compact-filter-content">
				<opportunities-map-filters
					v-if="activeFloatingFilter === FloatingFilterKey.Opportunities"
					ref="opportunitiesMapFiltersRef"
					@user-change="markUserChange"
				/>
				<transactions-map-filters
					v-else-if="activeFloatingFilter === FloatingFilterKey.Transactions"
					ref="transactionsMapFiltersRef"
					@user-change="markUserChange"
				/>
				<cadastre-filters
					v-else-if="activeFloatingFilter === FloatingFilterKey.Cadastre"
					ref="cadastreFiltersRef"
					@user-change="markUserChange"
				/>
			</div>
		</div>

		<floating-filter-panel
			v-else-if="activeFloatingFilter"
			:title="floatingFilterTitle"
			@close="activeFloatingFilter = null"
		>
			<template v-if="activeFloatingFilterCount > 0" #header-actions>
				<Button variant="ghost" size="xs" shape="pill" class="reset-filters-button" @click="resetActiveFloatingFilter">
					<span class="full-label">{{ t("filters.clear-filters") }}</span>
					<span class="short-label">{{ t("filters.clear-filters-short") }}</span>
				</Button>
			</template>
			<opportunities-map-filters
				v-if="activeFloatingFilter === FloatingFilterKey.Opportunities"
				ref="opportunitiesMapFiltersRef"
				@user-change="markUserChange"
			/>
			<transactions-map-filters
				v-else-if="activeFloatingFilter === FloatingFilterKey.Transactions"
				ref="transactionsMapFiltersRef"
				@user-change="markUserChange"
			/>
			<cadastre-filters
				v-else-if="activeFloatingFilter === FloatingFilterKey.Cadastre"
				ref="cadastreFiltersRef"
				@user-change="markUserChange"
			/>
		</floating-filter-panel>
	</div>
</template>

<script setup lang="ts">
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useResizeObserver } from "@vueuse/core"
import { storeToRefs } from "pinia"
import { computed, inject, nextTick, onMounted, ref, toRef, watch } from "vue"
import { useI18n } from "vue-i18n"
import legendFillUrl from "@/assets/images/menu-left/legend-fill.svg"
import legendHatchUrl from "@/assets/images/menu-left/legend-hatch.svg"
import legendOutlineUrl from "@/assets/images/menu-left/legend-outline.svg"
import AccordionItem from "@/components/ui/AccordionItem.vue"
import AccordionRoot from "@/components/ui/AccordionRoot.vue"
import ArkBaseSwitch from "@/components/ui/ArkBaseSwitch.vue"
import Button from "@/components/ui/Button.vue"
import LoginCta from "@/components/ui/LoginCta.vue"
import ExtendedAccessModal from "@/components/modals/ExtendedAccessModal.vue"
import CadastreFilters from "@/components/filtering/CadastreFilters.vue"
import FloatingFilterPanel from "@/components/filtering/FloatingFilterPanel.vue"
import LayerInfoPopover from "@/components/layers/LayerInfoPopover.vue"
import OpportunitiesMapFilters from "@/components/filtering/OpportunitiesMapFilters.vue"
import TransactionsMapFilters from "@/components/filtering/TransactionsMapFilters.vue"
import ProjectPicker from "@/components/layers/ProjectPicker.vue"
import SavedSearchCard from "@/components/saved-searches/SavedSearchCard.vue"
import SavedSearchesPanel from "@/components/saved-searches/SavedSearchesPanel.vue"
import { useNavigationContext } from "@/composables/useNavigationContext"
import { useSavedSearches } from "@/composables/useSavedSearches"
import { useView } from "@/composables/useView"
import { activeFilterFieldCount } from "@/views/viewFilterDefaults"
import { ProjectType } from "@/graphql"
import { LayerType, type LayerTypeValue } from "@/model/DataModel.ts"
import { Permission, UseRootStore } from "@/model/RootStore"
import { getMapModeStorageItem, isHuntingMode, ProjectStoreKey, setMapModeStorageItem } from "@/stores/projectStore.ts"
import { useUIStore } from "@/stores/uiStore"
import { useExtendedAccessStore } from "@/stores/extendedAccessStore"
import {
	buildBadgeIconDataUri,
	buildOpportunityIconDataUri,
	IconBadge,
	resolveIconFamily,
} from "@/utils/opportunityIcons"
import {
	analytics,
	type BooleanLayerFilterGroup,
	type BooleanLayerFilterKey,
	type DevPotentialKey,
	type UrbanPriorityKey,
	type UrbanPriorityStatus,
} from "@/utils/analytics"
import { CtaSource } from "@/utils/ctaSource"
import { ViewScope } from "@/types"

const props = defineProps({
	currentMapStyle: {
		type: String,
		required: true,
	},
	// renders the panel for the mobile bottom sheet: peek header, in-sheet filter view, no side-panel chrome
	compact: {
		type: Boolean,
		default: false,
	},
	// compact sheet dragged past its peek snap point; the list only scrolls once expanded
	expanded: {
		type: Boolean,
		default: false,
	},
	// compact sheet is showing; closing it resets the filter sub-view
	open: {
		type: Boolean,
		default: true,
	},
})

const currentMapStyle = toRef(props, "currentMapStyle")

const emit = defineEmits(["current-map-style", "layers-count", "user-change", "header-resize", "close", "filter-view"])

const { t } = useI18n()
const uiStore = useUIStore()

const isHuntingModeActive = computed(() => isHuntingMode())

const store = UseRootStore()
const { visibleLayers, filters, isUserLoggedIn, currentUser } = storeToRefs(store)
const _visibleLayers = ref<string[]>([...visibleLayers.value])
const canViewCompletedAssessments = computed(() => currentUser.value?.has(Permission.CompletedAssessmentsRead) ?? false)

const extendedAccessStore = useExtendedAccessStore()
const isExtendedAccessModalOpen = ref(false)
const extendedAccessSource = ref(CtaSource.Layers)

function openExtendedAccessModal(source: CtaSource): void {
	analytics.extendedAccessCtaClicked(source)
	extendedAccessSource.value = source
	isExtendedAccessModalOpen.value = true
}

watch(
	() => store.userApiToken,
	() => {
		extendedAccessStore.load()
	},
	{ immediate: true }
)
const showExtendedAccessCta = computed(() => isUserLoggedIn.value && extendedAccessStore.canStartExtendedAccess)

// Project scope determines which hunting views and opportunity types are active.
const projectStore = inject(ProjectStoreKey)!()
const currentProject = toRef(projectStore, "currentProject")
const opportunityView = useView(ViewScope.OpportunitiesMap)
const opportunityHuntingView = useView(ViewScope.OpportunitiesHuntingMap)
const transactionView = useView(ViewScope.TransactionsMap)
const transactionHuntingView = useView(ViewScope.TransactionsHuntingMap)
const parcelView = useView(ViewScope.ParcelsMap)
const parcelHuntingView = useView(ViewScope.ParcelsHuntingMap)
const { isMapMode, isHuntingSection } = useNavigationContext({ currentProject })
const isHuntingMapMode = computed(() => isMapMode.value && isHuntingSection.value)
const activeOpportunityView = computed(() => (isHuntingMapMode.value ? opportunityHuntingView : opportunityView))
const activeTransactionView = computed(() => (isHuntingMapMode.value ? transactionHuntingView : transactionView))
const activeParcelView = computed(() => (isHuntingMapMode.value ? parcelHuntingView : parcelView))
const isInProjectScope = ref(false)
const isProjectScopeDetermined = ref(false)

const isLegendExpanded = ref(false)

const isInCcaProject = computed(
	() =>
		isProjectScopeDetermined.value &&
		isInProjectScope.value &&
		projectStore.currentProject != null &&
		projectStore.currentProject.type !== ProjectType.Workspace
)

const canWriteTarget = computed(() => isUserLoggedIn.value && currentUser.value.has(Permission.OpportunityTargetWrite))

type LayerItem = { id: LayerTypeValue; label: string; details: string }
type OpportunityLayerType =
	| typeof LayerType.ListingLanderz
	| typeof LayerType.ListingPotential
	| typeof LayerType.OffMarketOfficial
	| typeof LayerType.OpportunityTarget

const DEFAULT_EXPLORATION_OPPORTUNITY_TYPES: OpportunityLayerType[] = [
	LayerType.ListingLanderz,
	LayerType.ListingPotential,
	LayerType.OffMarketOfficial,
]

const DEFAULT_HUNTING_OPPORTUNITY_TYPES: OpportunityLayerType[] = [
	LayerType.ListingLanderz,
	LayerType.ListingPotential,
	LayerType.OffMarketOfficial,
	LayerType.OpportunityTarget,
]

const opportunityLayerItems = computed<LayerItem[]>(() => {
	const orderedLayers = [
		LayerType.ListingLanderz,
		LayerType.ListingPotential,
		LayerType.OffMarketOfficial,
		LayerType.OpportunityTarget,
	]

	return orderedLayers
		.filter(l => {
			if (l === LayerType.OpportunityTarget) {
				return (
					isInCcaProject.value || (isProjectScopeDetermined.value && isInProjectScope.value && canWriteTarget.value)
				)
			}
			if (l === LayerType.ListingPotential || l === LayerType.OffMarketOfficial) {
				return (isUserLoggedIn.value && currentUser.value.has(Permission.OpportunityRead)) || isInCcaProject.value
			}
			return true
		})
		.map(id => ({
			id,
			label: t(`opportunities-layers.${id}`),
			details: t(`opportunities-layers-details.${id}`),
		}))
})

const defaultOpportunityTypes = computed(() => {
	const availableTypes = new Set(opportunityLayerItems.value.map(item => item.id))
	return (isHuntingMapMode.value ? DEFAULT_HUNTING_OPPORTUNITY_TYPES : DEFAULT_EXPLORATION_OPPORTUNITY_TYPES).filter(
		type => availableTypes.has(type)
	)
})

const visibleOpportunityTypes = computed(() => {
	const types =
		activeOpportunityView.value.view.value.filter?.opportunity?.opportunityTypes ?? defaultOpportunityTypes.value
	return isHuntingMode() ? types : types.filter(type => type !== LayerType.OpportunityTarget)
})

function isOpportunityLayerVisible(itemId: LayerTypeValue): boolean {
	return visibleOpportunityTypes.value.includes(itemId)
}

function isTransactionLayerVisible(): boolean {
	return filters.value.layers.includes(LayerType.Transaction)
}

onMounted(() => {
	projectStore.fetchCurrentProjectMetadata().then(project => {
		isInProjectScope.value = !!(project && project.value)
		isProjectScopeDetermined.value = true
	})
})

watch(
	() => projectStore.currentProject,
	project => {
		isInProjectScope.value = !!project
		isProjectScopeDetermined.value = true
	}
)

// logic for UX display that layers are selected
const activeLayersCount = computed(() => {
	return _visibleLayers.value.filter(layer => layer !== "opportunities" && layer !== "transactions").length
})

// active layer counts, shown as a badge on each section so it stays visible when the accordion is collapsed
const activeOpportunitiesCount = computed(
	() => opportunityLayerItems.value.filter(item => isOpportunityLayerVisible(item.id)).length
)

const activeTypedLayersCount = computed(
	() => activeOpportunitiesCount.value + (filters.value.layers.includes(LayerType.Transaction) ? 1 : 0)
)

// includes opportunity/transaction layer toggles, tracked separately from generic visible layers
const totalActiveLayersCount = computed(() => activeLayersCount.value + activeTypedLayersCount.value)

const opportunitiesFiltersCount = computed(() =>
	activeFilterFieldCount(activeOpportunityView.value.view.value.filter?.opportunity, ["opportunityTypes"])
)
const transactionsFiltersCount = computed(() =>
	activeFilterFieldCount(activeTransactionView.value.view.value.filter?.transaction)
)
const cadastreFiltersCount = computed(() => {
	const parcelFilter = activeParcelView.value.view.value.filter?.parcel
	return activeFilterFieldCount(parcelFilter, ["showStreets"]) + (parcelFilter?.showStreets ? 1 : 0)
})
const filtersCount = computed(
	() => opportunitiesFiltersCount.value + transactionsFiltersCount.value + cadastreFiltersCount.value
)

// saved search is opt-in behind a toggle, on by default; remembered across sessions in map mode only
const SAVED_SEARCH_ENABLED_STORAGE_KEY = "saved-search-enabled"

const persistedSavedSearchEnabled = getMapModeStorageItem(SAVED_SEARCH_ENABLED_STORAGE_KEY)
const savedSearchEnabled = ref(
	persistedSavedSearchEnabled === null ? !isHuntingMode() : persistedSavedSearchEnabled === "true"
)

watch(savedSearchEnabled, value => setMapModeStorageItem(SAVED_SEARCH_ENABLED_STORAGE_KEY, String(value)))

const {
	panelMode,
	isNamingSearch,
	isSavingSearch,
	isUpdatingSearch,
	newSearchName,
	saveError,
	savedSearches,
	isRestoringActiveSavedSearch,
	activeSavedSearch,
	isActiveSavedSearchModified,
	isUnsavedSearch,
	isEmptySearchState,
	currentSearchTitle,
	currentSearchSummary,
	resetCurrentSearch,
	deactivateCurrentSearch,
	cancelSavedSearchChanges,
	cancelSave,
	saveCurrentSearch,
	updateCurrentSearch,
	openSavedSearches,
	startSave,
	applySavedSearch,
	markActiveSavedSearchModifiedByUser,
} = useSavedSearches({
	filtersCount,
	layersCount: totalActiveLayersCount,
})

// turning the toggle off leaves the current saved search and its filters, so turning it back on starts blank
function onToggleSavedSearch(value: boolean): void {
	if (!value && activeSavedSearch.value) {
		resetCurrentSearch()
	} else if (!value) {
		deactivateCurrentSearch()
	}
	savedSearchEnabled.value = value
}

// floating filter panel, opened from the filter button of an accordion section
const FloatingFilterKey = Object.freeze({
	Opportunities: "opportunities",
	Transactions: "transactions",
	Cadastre: "cadastre",
} as const)
type FloatingFilterKey = (typeof FloatingFilterKey)[keyof typeof FloatingFilterKey]
const activeFloatingFilter = ref<FloatingFilterKey | null>(null)

const floatingFilterTitles: Record<FloatingFilterKey, string> = {
	[FloatingFilterKey.Opportunities]: "layers.sections.opportunities",
	[FloatingFilterKey.Transactions]: "layers.sections.transaction",
	[FloatingFilterKey.Cadastre]: "layers.sections.cadastre",
}

const floatingFilterTitle = computed(() =>
	activeFloatingFilter.value ? t(floatingFilterTitles[activeFloatingFilter.value]) : ""
)

const opportunitiesMapFiltersRef = ref<InstanceType<typeof OpportunitiesMapFilters> | null>(null)
const transactionsMapFiltersRef = ref<InstanceType<typeof TransactionsMapFilters> | null>(null)
const cadastreFiltersRef = ref<InstanceType<typeof CadastreFilters> | null>(null)

const activeFloatingFilterCount = computed(() => {
	switch (activeFloatingFilter.value) {
		case FloatingFilterKey.Opportunities:
			return opportunitiesFiltersCount.value
		case FloatingFilterKey.Transactions:
			return transactionsFiltersCount.value
		case FloatingFilterKey.Cadastre:
			return cadastreFiltersCount.value
		default:
			return 0
	}
})

function toggleFloatingFilter(key: FloatingFilterKey): void {
	activeFloatingFilter.value = activeFloatingFilter.value === key ? null : key
}

function resetActiveFloatingFilter() {
	switch (activeFloatingFilter.value) {
		case FloatingFilterKey.Opportunities:
			opportunitiesMapFiltersRef.value?.resetFilters()
			break
		case FloatingFilterKey.Transactions:
			transactionsMapFiltersRef.value?.resetFilters()
			break
		case FloatingFilterKey.Cadastre:
			cadastreFiltersRef.value?.resetFilters()
			break
	}
}

const activeAdministrativeBoundariesCount = computed(
	() =>
		[
			"administrative-divisions/postal-codes",
			"administrative-divisions/regions",
			"administrative-divisions/metropolitan-areas",
			"administrative-divisions/rcms",
			"administrative-divisions/municipalities",
			"administrative-divisions/boroughs",
		].filter(layer => _visibleLayers.value.includes(layer)).length
)

const isEndangeredSpeciesActive = computed(
	() =>
		_visibleLayers.value.includes("endangered-species") &&
		(filters.value.endangeredSpecies.threatenedOrVulnerable ||
			filters.value.endangeredSpecies.susceptible ||
			filters.value.endangeredSpecies.candidate ||
			filters.value.endangeredSpecies.masked)
)

const activeConstraintsCount = computed(
	() =>
		[
			"wetlands",
			"agricultural-zones",
			"soil-capability",
			"floodable-zones",
			"agricultural-inclusions-exclusions",
		].filter(layer => _visibleLayers.value.includes(layer)).length + (isEndangeredSpeciesActive.value ? 1 : 0)
)

const activeLandUsesCount = computed(
	() => ["urban-planning", "land-use"].filter(layer => _visibleLayers.value.includes(layer)).length
)

const activeTransportationCount = computed(
	() => ["transit-lines", "transit-stations"].filter(layer => _visibleLayers.value.includes(layer)).length
)

const isPlanningPolicyActive = computed(
	() =>
		_visibleLayers.value.includes("planning-policy") &&
		(filters.value.planningPolicy.tod ||
			filters.value.planningPolicy.ppu ||
			filters.value.planningPolicy.corridor ||
			filters.value.planningPolicy.strategicSector)
)

const isUrbanPrioritiesActive = computed(
	() =>
		_visibleLayers.value.includes("urban-priorities") &&
		(filters.value.urbanPriorities.pmad.active ||
			filters.value.urbanPriorities.pmad.draft ||
			filters.value.urbanPriorities.sad.active ||
			filters.value.urbanPriorities.sad.draft ||
			filters.value.urbanPriorities.pu.active ||
			filters.value.urbanPriorities.pu.draft ||
			filters.value.urbanPriorities.ppu.active ||
			filters.value.urbanPriorities.ppu.draft ||
			filters.value.urbanPriorities.pu_interview.active ||
			filters.value.urbanPriorities.pu_interview.draft ||
			filters.value.urbanPriorities.other.active ||
			filters.value.urbanPriorities.other.draft)
)

const activeUrbanizationCount = computed(
	() =>
		(_visibleLayers.value.includes("city-pv-documents") ? 1 : 0) +
		(_visibleLayers.value.includes("urban-perimeters") ? 1 : 0) +
		(_visibleLayers.value.includes("zoning") ? 1 : 0) +
		(isPlanningPolicyActive.value ? 1 : 0) +
		(isUrbanPrioritiesActive.value ? 1 : 0)
)

const constraintsInfoDescription = computed(() => {
	const items = [
		[Permission.WetlandsRead, "constraints-item-wetlands"],
		[Permission.AgriculturalZonesRead, "constraints-item-agricultural"],
		[Permission.SoilCapabilityRead, "constraints-item-soil-capability"],
		[Permission.FloodableZonesRead, "constraints-item-floodable"],
		[Permission.EndangeredSpeciesRead, "constraints-item-endangered-species"],
	] as const

	const accessible = items
		.filter(([permission]) => store.currentUser?.has(permission))
		.map(([, key]) => t(`layers.info-popover.${key}`))
	if (accessible.length === 0) return ""

	return `${t("layers.info-popover.constraints-description-intro")} ${accessible.join(", ")}.`
})

const urbanizationInfoDescription = computed(() => {
	const items = [
		[Permission.CityPvDocumentsRead, "urbanization-item-city-pv"],
		[Permission.PlanningPolicyRead, "urbanization-item-planning-policy"],
		[Permission.UrbanPerimetersRead, "urbanization-item-urban-perimeters"],
		[Permission.ZoningRead, "urbanization-item-zoning"],
		[Permission.UrbanPrioritiesRead, "urbanization-item-urban-priorities"],
	] as const

	const accessible = items
		.filter(([permission]) => store.currentUser?.has(permission))
		.map(([, key]) => t(`layers.info-popover.${key}`))
	if (accessible.length === 0) return ""

	return `${t("layers.info-popover.urbanization-description-intro")} ${accessible.join(", ")}.`
})

const activeDevelopmentPotentialCount = computed(() =>
	_visibleLayers.value.includes("prospects") &&
	(filters.value.developmentPotential.high ||
		filters.value.developmentPotential.medium ||
		filters.value.developmentPotential.low)
		? 1
		: 0
)

let syncingFromStore = false

// sync when a layer is activated externally (ex: during tagging)
watch(visibleLayers, newVal => {
	syncingFromStore = true
	_visibleLayers.value = [...newVal]
	nextTick(() => {
		syncingFromStore = false
	})
})

watch(_visibleLayers, newVal => {
	if (!syncingFromStore) {
		store.setVisibleLayers(newVal)
	}
})

const dependentLayers: Record<string, string[]> = {
	"urban-planning": ["land-use"],
}

function markUserChange(): void {
	emit("user-change")
	markActiveSavedSearchModifiedByUser()
}

const badgeLegendUris = {
	new: buildBadgeIconDataUri(IconBadge.New),
	offer: buildBadgeIconDataUri(IconBadge.Offer),
	liked: buildBadgeIconDataUri(IconBadge.Liked),
	discarded: buildBadgeIconDataUri(IconBadge.Discarded),
}

function layerIconDataUri(itemId: string): string | null {
	if (itemId === LayerType.Transaction) return "/icons/generated/transaction.png"

	const resolvedFamily = resolveIconFamily(itemId)
	return resolvedFamily ? buildOpportunityIconDataUri(resolvedFamily.color) : null
}

function toggleLayerItem(itemId: LayerTypeValue, isChecked: boolean): void {
	markUserChange()
	analytics.layerChanged(itemId, isChecked)
	const layers = new Set(filters.value.layers)

	if (isChecked) {
		layers.add(itemId)
	} else {
		layers.delete(itemId)
	}

	filters.value.layers = [...layers]
}

function toggleOpportunityLayerItem(itemId: LayerTypeValue, isChecked: boolean): void {
	markUserChange()
	analytics.layerChanged(itemId, isChecked)
	const types = new Set(visibleOpportunityTypes.value)

	if (isChecked) {
		types.add(itemId)
	} else {
		types.delete(itemId)
	}

	activeOpportunityView.value.setOpportunityTypes([...types])
}

function updateSelection(layer: string, isActive: boolean) {
	markUserChange()
	analytics.layerSelectionChanged(layer, isActive)
	const layers = [..._visibleLayers.value]
	const index = layers.indexOf(layer)

	if (isActive && index === -1) {
		layers.push(layer)
	} else if (!isActive && index !== -1) {
		layers.splice(index, 1)
		for (const sublayer of dependentLayers[layer] ?? []) {
			const subIndex = layers.indexOf(sublayer)
			if (subIndex !== -1) layers.splice(subIndex, 1)
		}
	}
	_visibleLayers.value = layers
}

function updateBooleanFilter<Group extends BooleanLayerFilterGroup>(
	group: Group,
	key: BooleanLayerFilterKey<Group>,
	checked: boolean
) {
	markUserChange()
	analytics.booleanLayerFilterChanged(group, key, checked)
	const target = filters.value[group] as Record<string, boolean>
	target[key] = checked
}

function updateUrbanPriority(priority: UrbanPriorityKey, checked: boolean) {
	markUserChange()
	analytics.urbanPriorityChanged(priority, checked)
	filters.value.urbanPriorities[priority].active = checked
	filters.value.urbanPriorities[priority].draft = checked
}

function updateUrbanPriorityStatus(priority: UrbanPriorityKey, status: UrbanPriorityStatus, checked: boolean) {
	markUserChange()
	analytics.urbanPriorityStatusChanged(priority, status, checked)
	filters.value.urbanPriorities[priority][status] = checked
}

function updateDevPotentials(id: DevPotentialKey, checked: boolean) {
	markUserChange()
	analytics.developmentPotentialChanged(id, checked)
	filters.value.developmentPotential[id] = checked
}

watch(
	activeLayersCount,
	count => {
		emit("layers-count", count)
	},
	{ immediate: true }
)

// compact (mobile sheet): height through the Transactions row, so the drawer peeks to exactly there
const accordionRootRef = ref<{ $el?: HTMLElement } | null>(null)
const peekEndEl = ref<HTMLElement | null>(null)

function reportPeekHeight(): void {
	if (!props.compact || !peekEndEl.value) return
	const height = peekEndEl.value.offsetTop + peekEndEl.value.offsetHeight
	if (height > 0) emit("header-resize", height)
}

watch(activeFloatingFilter, filter => {
	if (props.compact) emit("filter-view", filter != null)
})

watch(
	() => props.open,
	isOpen => {
		if (props.compact && !isOpen) activeFloatingFilter.value = null
	}
)

// re-measure the peek when the list resizes (sections expanding, async hunting rows)
useResizeObserver(
	() => accordionRootRef.value?.$el ?? null,
	() => reportPeekHeight()
)
watch(
	() => props.compact,
	() => {
		void nextTick(reportPeekHeight)
	},
	{ immediate: true }
)
</script>

<style scoped>
.layers {
	background-color: var(--neutral-0);
	--padding-x: clamp(var(--spacing-md), 1.25vw, var(--spacing-lg));
	padding: var(--spacing-md) var(--padding-x);
	display: flex;
	flex-direction: column;
	height: 100%;
	--width-toggle: clamp(26px, 1.5625vw, 30px);
	--height-toggle: clamp(14px, 0.8333vw, 16px);

	.project-switcher-slot {
		flex-shrink: 0;
		margin-bottom: var(--spacing-md);
	}

	.project-switcher-separator {
		flex-shrink: 0;
		height: var(--border-width-thin);
		margin: 0 calc(var(--padding-x) * -1) var(--spacing-md);
		background-color: var(--neutral-200);
	}

	.header {
		flex-shrink: 0;
		margin-bottom: var(--spacing-md);
		padding-left: 4px;
		position: relative;
	}

	.title {
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-medium);
		font-weight: var(--font-weight-medium);
		line-height: var(--line-height-normal);
		margin-bottom: var(--spacing-xs);
	}

	.subtitle {
		color: var(--neutral-600);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-regular);
		line-height: var(--line-height-normal);
	}

	.close-button {
		position: absolute;
		top: 0;
		right: 0;
		padding: var(--spacing-sm);
		background: none;
		border: none;
		cursor: pointer;
		color: var(--neutral-600);
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: var(--border-radius-sm);
		transition:
			background 0.15s ease,
			color 0.15s ease;

		&:hover {
			background: var(--neutral-100);
			color: var(--neutral-900);
		}

		svg {
			display: block;
		}
	}

	.content {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		min-height: 0;
		padding-bottom: var(--spacing-lg);

		.login-cta {
			margin-top: var(--spacing-sm);
		}

		.extended-access-cta {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-sm);
			margin-top: var(--spacing-sm);
			padding: var(--spacing-sm) var(--spacing-md);
			border: var(--border-width-thin) solid var(--neutral-100);
			border-radius: var(--border-radius-lg);
			background: color-mix(in srgb, var(--neutral-50) 50%, transparent);

			p {
				margin: 0;
				color: var(--neutral-600);
				font-size: var(--font-size-xs);
			}

			.action {
				width: 100%;
				gap: var(--spacing-xs);
			}
		}
		padding-right: var(--spacing-xs);
		margin-right: calc(var(--spacing-xs) * -1);
		scrollbar-width: none;
		-ms-overflow-style: none;
	}

	.saved-search-footer {
		position: relative;
		flex-shrink: 0;
		margin: 0 calc(var(--padding-x) * -1);
		padding: var(--spacing-md) var(--padding-x) 0;
		border-top: var(--border-width-thin) solid var(--neutral-200);
		box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.04);

		.saved-search-section {
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-lg);
			background: var(--neutral-0);
			box-shadow: var(--shadow-sm);
			overflow: hidden;
		}

		.saved-search-toggle {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-sm);
			min-height: 34px;
			padding: var(--spacing-xxs) var(--spacing-md);

			.label {
				display: flex;
				align-items: center;
				gap: var(--spacing-sm);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-regular);
				font-weight: var(--font-weight-medium);
				color: var(--neutral-900);
			}

			.icon {
				width: var(--size-icon-lg);
				height: var(--size-icon-lg);
				fill: var(--neutral-900);
				flex-shrink: 0;
			}
		}

		.saved-search-card.nested {
			border: none;
			border-radius: 0;
			background: transparent;
			box-shadow: none;
			border-top: var(--border-width-thin) solid var(--neutral-200);
		}

		.saved-search-loading {
			margin: 0;
			padding: var(--spacing-md);
			border-top: var(--border-width-thin) solid var(--neutral-200);
			color: var(--neutral-600);
			font-size: var(--font-size-small);
		}
	}
}

.filter-button {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--size-button);
	height: var(--size-button);
	border: 1px solid var(--neutral-100);
	border-radius: var(--border-radius-full);
	background: transparent;
	color: var(--neutral-600);
	cursor: pointer;

	&:hover {
		color: var(--neutral-900);
		background: var(--neutral-50);
	}

	&.active {
		color: var(--primary-green);
		border-color: var(--primary-green);
		background: var(--neutral-50);
	}

	svg {
		width: var(--size-icon-md);
		height: var(--size-icon-md);
		stroke: currentColor;
		fill: none;
		stroke-width: 1.5;
		stroke-linejoin: round;
	}

	&.has-active-filters::after {
		content: "";
		position: absolute;
		top: 0;
		right: 0;
		width: var(--spacing-sm);
		height: var(--spacing-sm);
		border-radius: var(--border-radius-full);
		background: var(--primary-green);
		border: var(--border-width-thin) solid var(--neutral-0);
	}
}

.reset-filters-button {
	white-space: nowrap;
	align-self: center;
	line-height: 1;

	.short-label {
		display: none;
	}

	@media (max-width: 1510px) {
		.full-label {
			display: none;
		}

		.short-label {
			display: inline;
		}
	}
}

.filterable-layers {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);

	&[data-tour-highlighted] .filter-button {
		color: var(--primary-green);
		border-color: var(--primary-green);
	}
}

.layer-selectors {
	.layer-selector {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		cursor: pointer;
		margin-bottom: var(--spacing-md);
		gap: var(--spacing-md);

		span {
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-regular);
			line-height: var(--line-height-normal);
			max-width: var(--width-layers);

			&.closed {
				color: var(--neutral-600);
			}
		}

		.layer-selector-label {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);

			img {
				width: var(--size-icon-lg);
				height: var(--size-icon-lg);
				object-fit: contain;
				flex-shrink: 0;
			}
		}

		&.locked {
			.layer-selector-label {
				gap: var(--spacing-xs);
			}

			.lock-icon {
				flex-shrink: 0;
				color: var(--neutral-600);
			}

			:deep(.slider) {
				cursor: pointer;
			}
		}
	}

	.opportunity-legend {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		padding-top: var(--spacing-md);
		padding-bottom: var(--spacing-md);
		border-top: 1px solid var(--neutral-200);

		.legend-title {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
			margin-left: var(--spacing-xs);
			padding: 0;
			border: none;
			background: none;
			cursor: pointer;
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-medium);
			text-transform: uppercase;
			letter-spacing: 0.04em;
			color: var(--neutral-600);

			.legend-chevron {
				transition: transform 0.15s ease;
			}
		}

		&.collapsed .legend-chevron {
			transform: rotate(-90deg);
		}

		.legend-title + .legend-row {
			margin-top: var(--spacing-xs);
		}

		.legend-row {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);

			img {
				width: var(--size-icon-md);
				height: var(--size-icon-md);
			}

			span {
				font-size: var(--font-size-xs);
				color: var(--neutral-800);
			}
		}
	}

	.layer-subselectors {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		gap: var(--spacing-md);

		&:last-child {
			padding-bottom: var(--spacing-md);
		}

		.layer-subselector {
			display: flex;
			justify-content: space-between;
			margin-left: clamp(29.44px, 1.6667vw, 32px); /* 92% floor */
			gap: var(--spacing-md);

			span {
				color: var(--neutral-900);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-regular);
				font-weight: var(--font-weight-regular);
				line-height: var(--line-height-normal);
				max-width: var(--width-layers);
			}
		}

		.urban-priority-substatus {
			padding-left: clamp(55.2px, 3.125vw, 60px); /* 92% floor */
			display: flex;
			flex-direction: column;
			gap: var(--spacing-md);
			margin-top: var(--spacing-xs);

			.urban-priority-substatus-item {
				display: flex;
				justify-content: space-between;
				align-items: center;
				font-size: var(--font-size-small);

				&.draft {
					opacity: 0.5;
				}
			}
		}
	}

	.planning-policy-subselectors,
	.endangered-species-subselectors,
	.transportation-subselectors {
		padding-bottom: var(--spacing-md);
	}
}

.layers.compact {
	position: relative;
	--padding-x: var(--spacing-md);

	.header {
		margin-bottom: var(--spacing-sm);

		.title {
			font-size: var(--font-size-regular);
		}
	}

	.content.peek-locked {
		overflow-y: hidden;
	}

	.peek-end-marker {
		height: var(--spacing-md);
	}

	.content:not(.peek-locked) .peek-end-marker {
		height: 0;
		margin-bottom: calc(-1 * var(--spacing-sm));
	}
}

.compact-filter-view {
	position: absolute;
	inset: 0;
	z-index: 2;
	display: flex;
	flex-direction: column;
	background-color: var(--neutral-0);

	.compact-filter-header {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);
		padding: var(--spacing-sm) var(--spacing-md);

		border-bottom: var(--border-width-thin) solid var(--neutral-200);

		.back-button {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			padding: var(--spacing-xs);
			margin-left: calc(-1 * var(--spacing-xs));
			border: none;
			background: none;
			cursor: pointer;
			color: var(--neutral-600);
		}

		.title {
			flex: 1;
			min-width: 0;
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-medium);
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			padding-top: var(--spacing-xs);
		}
	}

	.compact-filter-content {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		padding: var(--spacing-md) var(--spacing-lg);
	}
}

.saved-search-swap-enter-active,
.saved-search-swap-leave-active {
	transition: opacity 0.15s ease;
}

.saved-search-swap-enter-from,
.saved-search-swap-leave-to {
	opacity: 0;
}
</style>
